import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePatchObservationForm } from "src/features/observationsAdmin/hooks";
import { useAdminForm } from "src/hooks/admin/useAdminForm";
import { CreateNewObservationForm } from "src/features/observationsAdmin/components/CreateNewObservationForm";
import { toast } from "react-toastify";
import { Schema } from "src/features/observationsAdmin/components/CreateNewObservationForm/schema";
import CustomToast from "src/components/CustomToast/CustomToast";

interface EditObservationFormProps {}

const EditObservationForm = ({}: EditObservationFormProps) => {
	const navigate = useNavigate();

	const [searchParams] = useSearchParams();

	const params = useParams<{ formId: number }>();
	const { formId } = params;
	const categoryId = searchParams.get("categoryId") || "";

	const { data: form, isLoading: isLoadingForm } = useAdminForm(formId);
	const { mutate: updateForm } = usePatchObservationForm();

	const [defaultValues, setDefaultValues] = useState<Schema>({
		formName: "",
		category: parseInt(categoryId) || undefined,
		observationType: "",
		sendEmail: true,
		observationRecipients: [],
		gradingRecipients: [],
		observationSpecificEmailAddress: "",
		gradingSpecificEmailAddress: "",
		customEmail: false,
		customEmailBody: "",
		customEmailSubject: "",
		customEmailTitle: "",
		limitToCurrentUser: false
	});

	useEffect(() => {
		if (!form) return;

		const formData: Schema = {
			formName: form.name,
			// 1 is the id of the "Undefined" category
			category: form.category === 1 ? undefined : form.category,
			observationType: form.observation_type,
			sendEmail: form.send_email,
			observationRecipients: form.observation_recipients,
			gradingRecipients: form.grading_recipients,
			observationSpecificEmailAddress: form.observation_specific_email_address,
			gradingSpecificEmailAddress: form.grading_specific_email_address,
			customEmail: form.custom_email,
			customEmailTitle: form.custom_email_title,
			customEmailSubject: form.custom_email_subject,
			customEmailBody: form.custom_email_body,
			limitToCurrentUser: form.limit_to_current_user
		};

		setDefaultValues(formData);
	}, [form]);

	const handleOnValid = (data: Schema) => {
		if (!data.observationType) return;

		updateForm(
			{
				id: params.formId,
				name: data.formName,
				category: data.category,
				observation_type: data.observationType,
				send_email: data.sendEmail,
				grading_recipients: data.gradingRecipients,
				grading_specific_email_address: data.gradingSpecificEmailAddress,
				observation_recipients: data.observationRecipients,
				observation_specific_email_address:
					data.observationSpecificEmailAddress,
				custom_email: data.customEmail,
				custom_email_body: data.customEmailBody,
				custom_email_subject: data.customEmailSubject,
				custom_email_title: data.customEmailTitle,
				draft: true,
				published: false,
				archived: false,
				limit_to_current_user: data.limitToCurrentUser
			},
			{
				onSuccess: (form) => {
					const state = {
						observationFormId: form.id
					};

					navigate(`/observations/forms/${form.id}/questions`, { state });
				},
				onError: () => {
					toast.error(
						<CustomToast
							type="Error"
							message="There is an error updating the form"
						/>,
						{
							autoClose: false
						}
					);
				}
			}
		);
	};

	return (
		<div className="px-6 py-10 max-w-3xl">
			<h1 className="text-3xl font-semibold mb-6">Edit form</h1>

			{isLoadingForm ? (
				<div
					className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin"
					aria-label="Loading..."
				>
					<span className="sr-only">Loading...</span>
				</div>
			) : (
				<CreateNewObservationForm
					defaultValues={defaultValues}
					isObservationTypeEditable={false}
					onValid={handleOnValid}
				/>
			)}
		</div>
	);
};

export { EditObservationForm, type EditObservationFormProps };
